import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  normalizeExternalMidiPatch,
  type InstrumentSlot,
} from "../../../../../src/lib/audio";
import { useStoreSelector } from "../../../../../src/lib/store";
import { getLabActions } from "../../labActions";
import { projectStore } from "../../store/projectStore";
import { formatOctaveLabel, MusicalKeyboard } from "../MusicalKeyboard";

/**
 * `kind: "externalMidi"` — output port + channel; silent in Adam, notes over Web MIDI.
 */
export function ExternalMidiEditor({ slot }: { slot: InstrumentSlot }) {
  const instruments = useStoreSelector(projectStore, s => s.instruments);
  const instrument = instruments[slot];
  const patch =
    instrument?.kind === "externalMidi"
      ? normalizeExternalMidiPatch(instrument.patch)
      : null;
  const panelRef = useRef<HTMLDivElement>(null);
  const [octaveShift, setOctaveShift] = useState(0);
  const [outputs, setOutputs] = useState<Array<{ id: string; name: string }>>([]);
  const [midiOk, setMidiOk] = useState(true);

  useLayoutEffect(() => {
    getLabActions()?.setInstrumentSlot?.(slot);
    setOctaveShift(0);
  }, [slot]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const ok = (await getLabActions()?.refreshExternalMidiAccess?.()) ?? false;
      if (cancelled) {
        return;
      }
      setMidiOk(ok);
      setOutputs(getLabActions()?.listExternalMidiOutputs?.() ?? []);
    })();
    return () => {
      cancelled = true;
    };
  }, [slot]);

  if (!patch) {
    return (
      <div className="muted flex min-h-0 flex-1 flex-col overflow-hidden px-3 py-2 text-[12px]">
        Slot is not an External MIDI instrument.
      </div>
    );
  }

  function commit(partial: Partial<typeof patch>): void {
    if (!patch) {
      return;
    }
    getLabActions()?.updateInstrumentExternalMidiPatch?.(slot, {
      ...patch,
      ...partial,
    });
  }

  return (
    <div ref={panelRef} className="flex min-h-0 flex-1 flex-col overflow-hidden outline-none" tabIndex={0}>
      <div className="min-h-0 flex-1 overflow-auto px-3 py-2 text-[12px]">
        {!midiOk && (
          <p className="mb-2 text-[11px] text-amber-300/90">
            Web MIDI unavailable in this browser — enable a MIDI-capable browser / permission.
          </p>
        )}
        <label className="mb-2 grid grid-cols-[4.5rem_1fr] items-center gap-2 text-[11px] text-lab-muted">
          <span>Output</span>
          <select
            className="h-7 rounded-md border border-lab-line bg-lab-panel-deep px-2 text-[11px] text-lab-text"
            value={patch.outputDeviceId}
            aria-label="MIDI output"
            onChange={event => commit({ outputDeviceId: event.target.value })}
          >
            <option value="">(none)</option>
            {outputs.map(port => (
              <option key={port.id} value={port.id}>
                {port.name}
              </option>
            ))}
          </select>
        </label>
        <label className="mb-2 grid grid-cols-[4.5rem_1fr] items-center gap-2 text-[11px] text-lab-muted">
          <span>Channel</span>
          <select
            className="h-7 rounded-md border border-lab-line bg-lab-panel-deep px-2 text-[11px] text-lab-text"
            value={patch.channel}
            aria-label="MIDI channel"
            onChange={event => commit({ channel: Number(event.target.value) })}
          >
            {Array.from({ length: 16 }, (_, i) => i + 1).map(ch => (
              <option key={ch} value={ch}>
                {ch}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="shrink-0 border-t border-white/[0.06] bg-lab-panel-bar px-2 py-2">
        <div className="mb-1 flex items-center justify-between text-[10px] text-lab-muted">
          <span>Audition · {formatOctaveLabel(octaveShift)}</span>
        </div>
        <MusicalKeyboard
          key={slot}
          focusHostRef={panelRef}
          baseMidi={60 + octaveShift * 12}
          octaves={2}
          voiceMode="poly"
          onNoteOn={(note, velocity) => getLabActions()?.instrumentNoteOn?.(slot, note, velocity)}
          onNoteOff={note => getLabActions()?.instrumentNoteOff?.(slot, note)}
          onOctaveChange={setOctaveShift}
        />
      </div>
    </div>
  );
}
